import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AnalyticsDashboard = ({ formData, onExport, onBack }) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const revenueData = months?.map((month, index) => ({
    month,
    revenue: parseInt(formData?.monthlyRevenue?.[index] || 0),
    forecast: parseInt(formData?.monthlyRevenue?.[index] || 0) * 1.15
  }));

  const forecastData = [
    { period: 'Current', revenue: 1200000, type: 'Actual' },
    { period: '6 Months', revenue: 1450000, type: 'Forecast' },
    { period: '12 Months', revenue: 1680000, type: 'Forecast' },
    { period: '24 Months', revenue: 2340000, type: 'Forecast' }
  ];

  const financialStabilityScore = 78;

  const aiRecommendations = [
    {
      title: 'Optimize Cash Flow Management',
      description: 'Your cash reserve of ' + formData?.cashReserve + ' months is healthy. Consider investing excess cash in short-term growth initiatives.',
      impact: 'High',
      priority: 'high'
    },
    {
      title: 'Reduce Customer Acquisition Cost',
      description: 'Focus on organic growth channels and referral programs to improve CAC efficiency by 25%.',
      impact: 'Medium',
      priority: 'medium'
    },
    {
      title: 'Diversify Revenue Streams',
      description: 'Your top product contributes significantly. Consider launching complementary offerings to reduce concentration risk.',
      impact: 'High',
      priority: 'high'
    },
    {
      title: 'Seasonal Strategy Optimization',
      description: formData?.isSeasonal === 'yes' ? 'Leverage peak months with targeted campaigns and prepare inventory accordingly.' : 'Maintain consistent marketing throughout the year.',
      impact: 'Medium',
      priority: 'medium'
    }
  ];

  const riskLevel = formData?.regulatoryRisk === 'high' || formData?.competitorPressure === 'high' ? 'High' : 
                    formData?.regulatoryRisk === 'medium' || formData?.competitorPressure === 'medium' ? 'Moderate' : 'Low';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Analytics Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-body">
              AI-powered financial forecasts and growth insights
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onBack}
              iconName="ChevronLeft"
              iconPosition="left"
            >
              Back to Form
            </Button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground font-body mb-1">Financial Stability Score</p>
              <p className="text-4xl font-heading font-bold text-foreground">{financialStabilityScore}/100</p>
            </div>
            <div className="w-24 h-24 rounded-full border-8 border-primary flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">{financialStabilityScore}%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-success/10 rounded-md">
              <Icon name="TrendingUp" size={20} color="var(--color-success)" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">12-Month Forecast</p>
          </div>
          <p className="text-2xl font-heading font-bold text-foreground mb-1">₹16.8L</p>
          <p className="text-xs text-success">+28.5% growth projected</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-primary/10 rounded-md">
              <Icon name="Target" size={20} color="var(--color-primary)" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">24-Month Forecast</p>
          </div>
          <p className="text-2xl font-heading font-bold text-foreground mb-1">₹23.4L</p>
          <p className="text-xs text-primary">+45.2% growth projected</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-md ${
              riskLevel === 'High' ? 'bg-destructive/10' : 
              riskLevel === 'Moderate' ? 'bg-warning/10' : 'bg-success/10'
            }`}>
              <Icon name="AlertTriangle" size={20} color={
                riskLevel === 'High' ? 'var(--color-destructive)' : 
                riskLevel === 'Moderate' ? 'var(--color-warning)' : 'var(--color-success)'
              } />
            </div>
            <p className="text-sm font-medium text-muted-foreground">Risk Level</p>
          </div>
          <p className={`text-2xl font-heading font-bold mb-1 ${
            riskLevel === 'High' ? 'text-destructive' : 
            riskLevel === 'Moderate' ? 'text-warning' : 'text-success'
          }`}>{riskLevel}</p>
          <p className="text-xs text-muted-foreground">Based on market analysis</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Revenue Trend (12 Months)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => `₹${value?.toLocaleString()}`} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Actual Revenue" />
              <Line type="monotone" dataKey="forecast" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Forecast" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Profit Projection</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip formatter={(value) => `₹${value?.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {formData?.isSeasonal === 'yes' && formData?.peakMonths?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Calendar" size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-heading font-semibold text-foreground">Seasonal Adjustment Suggestion</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Your business shows seasonal patterns with peak months in: {formData?.peakMonths?.join(', ')}
          </p>
          <div className="p-4 bg-accent/10 rounded-md">
            <p className="text-sm text-foreground">
              <strong>Recommendation:</strong> Increase inventory and marketing budget by 30-40% during peak months. 
              Prepare cash reserves 2 months in advance to capitalize on high-demand periods.
            </p>
          </div>
        </div>
      )}

      {formData?.majorClientDependency === 'yes' && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
            <h3 className="text-lg font-heading font-semibold text-destructive">Overdependency Alert</h3>
          </div>
          <p className="text-sm text-foreground">
            Your business shows dependency on a major client. This creates concentration risk. 
            Diversify your client base to reduce vulnerability and ensure sustainable growth.
          </p>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Sparkles" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-heading font-semibold text-foreground">AI Strategic Growth Recommendations</h3>
        </div>
        <div className="space-y-4">
          {aiRecommendations?.map((rec, index) => (
            <div key={index} className="p-4 bg-muted/50 rounded-md border border-border">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-semibold text-foreground">{rec?.title}</h4>
                <span className={`text-xs px-2 py-1 rounded ${
                  rec?.priority === 'high' ? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'
                }`}>
                  {rec?.impact} Impact
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{rec?.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Export Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => onExport('pdf')}
            className="flex items-center justify-between p-4 border-2 border-border rounded-md hover:border-primary transition-smooth group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-destructive/10 rounded-md">
                <Icon name="FileText" size={20} color="var(--color-destructive)" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-foreground">Download PDF Report</p>
                <p className="text-xs text-muted-foreground">Complete analysis with charts</p>
              </div>
            </div>
            <Icon name="Lock" size={20} color="var(--color-muted-foreground)" />
          </button>

          <button
            onClick={() => onExport('excel')}
            className="flex items-center justify-between p-4 border-2 border-border rounded-md hover:border-primary transition-smooth group"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-md">
                <Icon name="FileSpreadsheet" size={20} color="var(--color-success)" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-foreground">Download Excel Forecast</p>
                <p className="text-xs text-muted-foreground">Editable financial projections</p>
              </div>
            </div>
            <Icon name="Lock" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>
        <p className="text-xs text-center text-muted-foreground mt-4">
          🔒 Premium feature - Upgrade to unlock exports
        </p>
      </div>

      <div className="bg-accent/10 border border-accent/20 rounded-lg p-6 mt-6">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} color="var(--color-accent)" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">Scenario Simulation Mode – Coming Soon</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Dynamically recalculate forecasts based on different scenarios:
            </p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Increase marketing by 20%</li>
              <li>• Launch new product</li>
              <li>• Increase price by 10%</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;